/**
 * Helpers local to the Settings / Contacts / Outbox screens: strings the spec keeps out of the
 * dictionary and small pure functions the shared layer (`$lib`) does not provide. Each export
 * names the gap it covers; the stream report lists them.
 */
import { t, type I18nKey, type Lang } from '$lib/i18n';
import type { NotifyPerm } from '$lib/store';
import type { AutoAdvance } from '$lib/types';

/** Spec §12: the language row is labelled literally, in both languages at once. */
export const LANGUAGE_LABEL = 'اللغة / Language';

/** Undo-send window choices in seconds; `0` renders as `undoOff` (spec §12, `data-sec`). */
export const UNDO_CHOICES: readonly number[] = [0, 5, 10, 20];

/** After-action choices with their dictionary keys (spec §12, `data-adv`). */
export const ADVANCE_CHOICES: readonly { value: AutoAdvance; key: I18nKey }[] = [
  { value: 'list', key: 'advList' },
  { value: 'next', key: 'advNext' },
  { value: 'prev', key: 'advPrev' },
];

/** Spec §13: the contacts empty state is local to the component, not in the dictionary. */
export function contactsEmptyStrings(lang: Lang): { title: string; hint: string } {
  return lang === 'en'
    ? { title: 'No contacts yet', hint: 'Add an address with the form above, or import your whole book from the old webmail.' }
    : { title: 'لا جهات اتصال بعد', hint: 'أضف عنوانًا من النموذج أعلاه، أو استورد دفترك من البريد القديم دفعة واحدة.' };
}

export interface Identity {
  name: string;
  sig: string;
}

/**
 * kv `identities` is a JSON array of `{name, sig}`; kv `signature` (the first signature) is the
 * fallback when no identity list was ever saved. Always returns at least one row.
 */
export function parseIdentities(raw: string | null | undefined, signature: string | null | undefined): Identity[] {
  let list: Identity[] = [];
  if (raw) {
    try {
      const v: unknown = JSON.parse(raw);
      if (Array.isArray(v)) {
        list = v
          .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
          .map((x) => ({ name: typeof x.name === 'string' ? x.name : '', sig: typeof x.sig === 'string' ? x.sig : '' }));
      }
    } catch {
      /* corrupt value: fall back to the bare signature */
    }
  }
  return list.length ? list : [{ name: '', sig: signature || '' }];
}

/** Spec §12 save: extra identities that are completely empty are dropped; the first always stays. */
export function compactIdentities(list: readonly Identity[]): Identity[] {
  const trimmed = list.map((x) => ({ name: x.name.trim(), sig: x.sig }));
  const kept = trimmed.filter((x, i) => i === 0 || x.name !== '' || x.sig.trim() !== '');
  return kept.length ? kept : [{ name: '', sig: '' }];
}

/** Spec §12 notifications hint, chosen by state (denied → unsupported → iOS install → on → ask). */
export function notifyHint(o: { perm: NotifyPerm; canPush: boolean; needsInstall: boolean; on: boolean }): string {
  if (o.perm === 'denied') return `${t('notifyDenied')} · ${t('notifyFirefox')}`;
  if (o.perm === 'unsupported' || !o.canPush) return t('notifyUnsupported');
  if (o.needsInstall && !o.on) return t('pushInstallFirst');
  if (o.on) return t('pushPerDevice');
  return `${t('notifyAsk')} · ${t('pushPerDevice')}`;
}

/**
 * Outbox item state → label. `PendingSend` carries no `state` field and the dictionary has no
 * per-state keys, so the known states map onto the closest existing strings and anything else is
 * shown verbatim.
 */
export function outboxStateLabel(state: string): string {
  switch (state) {
    case 'queued':
      return t('scheduled');
    case 'sending':
      return t('sending');
    case 'failed':
      return t('sendFailed');
    default:
      return state;
  }
}

/** Quota percentage clamped to 0–100 for the progress bar. */
export function quotaPercent(used: number, limit: number): number {
  if (!limit) return 0;
  return Math.max(0, Math.min(100, Math.round((used / limit) * 100)));
}
